import {
  faChevronDown,
  faChevronUp,
  faClock,
  faEnvelope,
  faMapMarkerAlt,
  faPhone,
} from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Card,
  type CardProps,
  Divider,
  Flex,
  Menu,
  Spoiler,
  Text,
  Title,
} from '@mantine/core'
import clsx from 'clsx'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreAddress, type ParameterID } from '@flecto/types'
import { Icon, TextAddress, TextCopy } from '@flecto/ui'

import { useStoreAddresses } from '../../hooks/useStoreAddresses'
import { useStoreDetails } from '../../hooks/useStoreDetails'
import { TextBusinessHours } from '../TextBusinessHours/TextBusinessHours'
import styles from './styles.module.css'

export type CardStoreDetailsProps = Omit<CardProps, 'children' | 'title'> & {
  title?: ReactNode
  withCollapseInfo?: boolean
  onChangeAddress?: (address: CoreAddress) => void
}

export function CardStoreDetails({
  title,
  withCollapseInfo = false,
  onChangeAddress,
  ...props
}: CardStoreDetailsProps) {
  const { t } = useTranslation('store', { keyPrefix: 'store-shared' })

  const store = useStoreDetails()

  const [menuAddressesOpened, setMenuAddressesOpened] = useState(false)

  const [selectedAddressId, setSelectedAddressId] =
    useState<ParameterID | null>()

  const addresses = useStoreAddresses()
  const currentAddress = useMemo(() => {
    if (addresses.length === 0) {
      return store?.main_address || null
    }
    return addresses.find((a) => a.id === selectedAddressId) || null
  }, [addresses, selectedAddressId, store?.main_address])

  useEffect(() => {
    if (addresses.length > 0 && !selectedAddressId) {
      setSelectedAddressId((addresses ?? [])[0].id)
    }
  }, [addresses])

  useEffect(() => {
    if (currentAddress) {
      onChangeAddress?.(currentAddress)
    }
  }, [currentAddress])

  if (addresses.length <= 0 && !currentAddress) {
    return
  }

  const classes = clsx(styles.card, props.className)

  const getStoreInfoChildren = () => {
    const info = (
      <Box>
        <Flex gap='sm' align='center' mt='lg'>
          <Icon
            icon={faClock}
            withCircleBackground
            size='md'
            style={{ flexShrink: 0 }}
          />
          <Box w='100%'>
            <Text fw={500}>{t('card_store_details.label_opening_hours')}</Text>
            <TextBusinessHours
              address={currentAddress ?? store?.main_address}
            />
          </Box>
        </Flex>
        {store?.email && (
          <Flex gap='sm' align='center' mt='lg'>
            <Icon
              icon={faEnvelope}
              withCircleBackground
              size='md'
              style={{ flexShrink: 0 }}
            />
            <Box>
              <Text fw={500}>{t('card_store_details.label_email')}</Text>
              <TextCopy truncate>{store.email}</TextCopy>
            </Box>
          </Flex>
        )}
        {store?.phone_number && (
          <Flex gap='sm' align='center' mt='lg'>
            <Icon
              icon={faPhone}
              withCircleBackground
              size='md'
              style={{ flexShrink: 0 }}
            />
            <Box>
              <Text fw={500}>{t('card_store_details.label_phone_number')}</Text>
              <TextCopy truncate>{store.phone_number}</TextCopy>
            </Box>
          </Flex>
        )}
      </Box>
    )

    if (!withCollapseInfo) {
      return info
    }

    return (
      <Spoiler
        maxHeight={withCollapseInfo ? 5 : 1}
        showLabel={t('card_store_details.label_show_more')}
        hideLabel={<Box mt='md'>{t('card_store_details.label_hide')}</Box>}
      >
        {info}
      </Spoiler>
    )
  }

  return (
    <Card {...props} className={classes}>
      <Title order={4} lh={1} mt={2}>
        {title ?? t('card_store_details.title')}
      </Title>
      <Divider my='md' color='gray.3' />
      {currentAddress && (
        <Flex gap='sm' align='center'>
          <Icon
            icon={faMapMarkerAlt}
            withCircleBackground
            size='md'
            style={{ flexShrink: 0 }}
          />
          <Menu
            opened={menuAddressesOpened}
            onChange={setMenuAddressesOpened}
            withinPortal
          >
            <Menu.Target>
              <Flex style={{ cursor: 'pointer' }} gap='xs'>
                <TextAddress
                  withIcon={false}
                  className={styles.address}
                  address={currentAddress as CoreAddress}
                />
                <Icon
                  icon={!menuAddressesOpened ? faChevronDown : faChevronUp}
                  mt={3}
                />
              </Flex>
            </Menu.Target>
            <Menu.Dropdown maw={300}>
              {addresses.map((address) => (
                <Menu.Item
                  key={address.id}
                  onClick={() => setSelectedAddressId(address.id)}
                >
                  <TextAddress
                    address={address}
                    withIcon={false}
                    truncate={false}
                  />
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </Flex>
      )}
      {getStoreInfoChildren()}
    </Card>
  )
}
